import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Bug,
  BookOpen,
  Video,
  History,
  Plus,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  Eye,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const AdminToolsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('bugs');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [bugs, setBugs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [versions, setVersions] = useState([]);
  const [bugStats, setBugStats] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0 });
  
  // Dialog states
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  
  // Edit states
  const [editingBug, setEditingBug] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  
  // Filter states
  const [bugFilter, setBugFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [bugsRes, docsRes, tutorialsRes, versionsRes, statsRes] = await Promise.all([
        api.get('/bugs/'),
        api.get('/documentation/'),
        api.get('/tutorials/'),
        api.get('/versions/'),
        api.get('/bugs/stats/summary').catch(() => ({ data: { total: 0, new: 0, in_progress: 0, resolved: 0 } }))
      ]);
      
      setBugs(bugsRes.data || []);
      setDocs(docsRes.data || []);
      setTutorials(tutorialsRes.data || []);
      setVersions(versionsRes.data || []);
      setBugStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Bug handlers
  const handleUpdateBugStatus = async (bugId, status) => {
    try {
      await api.put(`/bugs/${bugId}`, { status });
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteBug = async (bugId) => {
    if (!confirm('Supprimer ce bug ?')) return;
    try {
      await api.delete(`/bugs/${bugId}`);
      toast.success('Bug supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Doc handlers
  const handleSaveDoc = async (docData) => {
    try {
      if (editingDoc) {
        await api.put(`/documentation/${editingDoc.id}`, docData);
        toast.success('Documentation mise à jour');
      } else {
        await api.post('/documentation/', docData);
        toast.success('Documentation créée');
      }
      setShowDocDialog(false);
      setEditingDoc(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm('Supprimer cette documentation ?')) return;
    try {
      await api.delete(`/documentation/${docId}`);
      toast.success('Documentation supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Tutorial handlers
  const handleSaveTutorial = async (tutorialData) => {
    try {
      if (editingTutorial) {
        await api.put(`/tutorials/${editingTutorial.id}`, tutorialData);
        toast.success('Tutoriel mis à jour');
      } else {
        await api.post('/tutorials/', tutorialData);
        toast.success('Tutoriel créé');
      }
      setShowTutorialDialog(false);
      setEditingTutorial(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteTutorial = async (tutorialId) => {
    if (!confirm('Supprimer ce tutoriel ?')) return;
    try {
      await api.delete(`/tutorials/${tutorialId}`);
      toast.success('Tutoriel supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Version handlers
  const handleSaveVersion = async (versionData) => {
    try {
      if (editingVersion) {
        await api.put(`/versions/${editingVersion.id}`, versionData);
        toast.success('Version mise à jour');
      } else {
        await api.post('/versions/', versionData);
        toast.success('Version créée');
      }
      setShowVersionDialog(false);
      setEditingVersion(null);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!confirm('Supprimer cette version ?')) return;
    try {
      await api.delete(`/versions/${versionId}`);
      toast.success('Version supprimée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const statusConfig = {
    new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
    in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: Clock },
    resolved: { label: 'Résolu', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    closed: { label: 'Fermé', color: 'bg-slate-100 text-slate-700', icon: CheckCircle }
  };

  const priorityConfig = {
    low: { label: 'Faible', color: 'bg-slate-100 text-slate-700' },
    medium: { label: 'Moyen', color: 'bg-amber-100 text-amber-700' },
    high: { label: 'Élevé', color: 'bg-orange-100 text-orange-700' },
    critical: { label: 'Critique', color: 'bg-red-100 text-red-700' }
  };

  const filteredBugs = bugs.filter(bug => {
    if (bugFilter !== 'all' && bug.status !== bugFilter) return false;
    if (searchQuery && !bug.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex bg-[var(--color-bg)]">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-800">Administration</h1>
              <p className="text-slate-500">Gérez les bugs, la documentation, les tutoriels et les versions</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full max-w-2xl mb-6">
                <TabsTrigger value="bugs" className="flex items-center gap-2">
                  <Bug className="w-4 h-4" />
                  <span className="hidden sm:inline">Bugs</span>
                  {bugStats.new > 0 && (
                    <Badge className="bg-red-500 text-white text-xs ml-1">{bugStats.new}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="docs" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">Docs</span>
                </TabsTrigger>
                <TabsTrigger value="tutorials" className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  <span className="hidden sm:inline">Tutoriels</span>
                </TabsTrigger>
                <TabsTrigger value="versions" className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Versions</span>
                </TabsTrigger>
              </TabsList>

              {/* Bugs Tab */}
              <TabsContent value="bugs">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Bug className="w-5 h-5 text-red-500" />
                          Gestion des bugs
                        </CardTitle>
                        <CardDescription>
                          {bugStats.total} bugs signalés • {bugStats.new} nouveaux • {bugStats.in_progress} en cours
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Rechercher..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 w-48"
                          />
                        </div>
                        <Select value={bugFilter} onValueChange={setBugFilter}>
                          <SelectTrigger className="w-32">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            <SelectItem value="new">Nouveaux</SelectItem>
                            <SelectItem value="in_progress">En cours</SelectItem>
                            <SelectItem value="resolved">Résolus</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                      </div>
                    ) : filteredBugs.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Bug className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucun bug signalé</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredBugs.map((bug) => {
                          const status = statusConfig[bug.status] || statusConfig.new;
                          const priority = priorityConfig[bug.priority] || priorityConfig.medium;
                          const StatusIcon = status.icon;
                          
                          return (
                            <div
                              key={bug.id}
                              className="p-4 border rounded-xl hover:border-slate-300 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-medium text-slate-800">{bug.title}</h3>
                                    <Badge className={cn("text-xs", status.color)}>
                                      <StatusIcon className="w-3 h-3 mr-1" />
                                      {status.label}
                                    </Badge>
                                    <Badge className={cn("text-xs", priority.color)}>
                                      {priority.label}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{bug.description}</p>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                    <span>Par {bug.reporter_name || bug.reporter_email}</span>
                                    <span>{new Date(bug.created_at).toLocaleDateString('fr-FR')}</span>
                                    {bug.page_url && (
                                      <a href={bug.page_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:underline">
                                        <ExternalLink className="w-3 h-3" />
                                        Voir la page
                                      </a>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Select
                                    value={bug.status}
                                    onValueChange={(value) => handleUpdateBugStatus(bug.id, value)}
                                  >
                                    <SelectTrigger className="w-32 h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="new">Nouveau</SelectItem>
                                      <SelectItem value="in_progress">En cours</SelectItem>
                                      <SelectItem value="resolved">Résolu</SelectItem>
                                      <SelectItem value="closed">Fermé</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteBug(bug.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              {bug.screenshot_url && (
                                <div className="mt-3">
                                  <img
                                    src={bug.screenshot_url}
                                    alt="Screenshot"
                                    className="h-24 object-cover rounded-lg border cursor-pointer hover:opacity-80"
                                    onClick={() => window.open(bug.screenshot_url, '_blank')}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documentation Tab */}
              <TabsContent value="docs">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-blue-500" />
                          Documentation
                        </CardTitle>
                        <CardDescription>{docs.length} articles de documentation</CardDescription>
                      </div>
                      <Button onClick={() => { setEditingDoc(null); setShowDocDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {docs.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucune documentation</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {docs.map((doc) => (
                          <div key={doc.id} className="p-4 border rounded-xl hover:border-slate-300 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-medium text-slate-800">{doc.title}</h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline">{doc.category}</Badge>
                                  {!doc.is_published && <Badge className="bg-amber-100 text-amber-700">Brouillon</Badge>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="icon" onClick={() => { setEditingDoc(doc); setShowDocDialog(true); }}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteDoc(doc.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tutorials Tab */}
              <TabsContent value="tutorials">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Video className="w-5 h-5 text-red-500" />
                          Tutoriels vidéo
                        </CardTitle>
                        <CardDescription>{tutorials.length} tutoriels disponibles</CardDescription>
                      </div>
                      <Button onClick={() => { setEditingTutorial(null); setShowTutorialDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {tutorials.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucun tutoriel</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {tutorials.map((tutorial) => (
                          <div key={tutorial.id} className="p-4 border rounded-xl hover:border-slate-300 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="w-24 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Video className="w-8 h-8 text-slate-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-slate-800 truncate">{tutorial.title}</h3>
                                <p className="text-sm text-slate-500 line-clamp-2">{tutorial.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline">{tutorial.category}</Badge>
                                  <a href={tutorial.youtube_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" /> YouTube
                                  </a>
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button variant="ghost" size="icon" onClick={() => { setEditingTutorial(tutorial); setShowTutorialDialog(true); }}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteTutorial(tutorial.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Versions Tab */}
              <TabsContent value="versions">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <History className="w-5 h-5 text-purple-500" />
                          Historique des versions
                        </CardTitle>
                        <CardDescription>Changelog de l'application</CardDescription>
                      </div>
                      <Button onClick={() => { setEditingVersion(null); setShowVersionDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle version
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {versions.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucune version enregistrée</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {versions.map((version, index) => (
                          <div key={version.id} className="relative pl-6 pb-4 border-l-2 border-slate-200 last:border-l-transparent">
                            <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-purple-500" />
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-purple-100 text-purple-700">v{version.version}</Badge>
                                  <span className="font-medium text-slate-800">{version.title}</span>
                                  {index === 0 && <Badge className="bg-green-100 text-green-700">Dernière</Badge>}
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  {new Date(version.release_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                                {version.changes && version.changes.length > 0 && (
                                  <ul className="mt-2 space-y-1">
                                    {version.changes.map((change, i) => (
                                      <li key={i} className="flex items-start gap-2 text-sm">
                                        <Badge className={cn("text-xs mt-0.5", 
                                          change.type === 'feature' ? 'bg-green-100 text-green-700' :
                                          change.type === 'fix' ? 'bg-blue-100 text-blue-700' :
                                          'bg-amber-100 text-amber-700'
                                        )}>
                                          {change.type === 'feature' ? 'Nouveau' : change.type === 'fix' ? 'Correction' : 'Amélioration'}
                                        </Badge>
                                        <span className="text-slate-600">{change.description}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="icon" onClick={() => { setEditingVersion(version); setShowVersionDialog(true); }}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteVersion(version.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>

      {/* Documentation Dialog */}
      <DocDialog
        open={showDocDialog}
        onOpenChange={setShowDocDialog}
        doc={editingDoc}
        onSave={handleSaveDoc}
      />

      {/* Tutorial Dialog */}
      <TutorialDialog
        open={showTutorialDialog}
        onOpenChange={setShowTutorialDialog}
        tutorial={editingTutorial}
        onSave={handleSaveTutorial}
      />

      {/* Version Dialog */}
      <VersionDialog
        open={showVersionDialog}
        onOpenChange={setShowVersionDialog}
        version={editingVersion}
        onSave={handleSaveVersion}
      />
    </div>
  );
};

// Documentation Dialog Component
const DocDialog = ({ open, onOpenChange, doc, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'features',
    content: '',
    order: 0,
    is_published: true,
    visible_to: ['client', 'team_member', 'agency']
  });

  const roleOptions = [
    { value: 'client', label: 'Clients' },
    { value: 'team_member', label: 'Membres équipe' },
    { value: 'agency', label: 'Agence' }
  ];

  useEffect(() => {
    if (doc) {
      setFormData({
        title: doc.title || '',
        category: doc.category || 'features',
        content: doc.content || '',
        order: doc.order || 0,
        is_published: doc.is_published !== false,
        visible_to: doc.visible_to || ['client', 'team_member', 'agency']
      });
    } else {
      setFormData({ title: '', category: 'features', content: '', order: 0, is_published: true, visible_to: ['client', 'team_member', 'agency'] });
    }
  }, [doc, open]);

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      visible_to: prev.visible_to.includes(role)
        ? prev.visible_to.filter(r => r !== role)
        : [...prev.visible_to, role]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doc ? 'Modifier' : 'Créer'} une documentation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="getting_started">Démarrage</SelectItem>
                <SelectItem value="features">Fonctionnalités</SelectItem>
                <SelectItem value="faq">FAQ</SelectItem>
                <SelectItem value="api">API</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visible pour</Label>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map(role => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => toggleRole(role.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                    formData.visible_to.includes(role.value)
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
                  )}
                >
                  {role.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">Sélectionnez les rôles qui pourront voir cette documentation</p>
          </div>
          <div className="space-y-2">
            <Label>Contenu (Markdown supporté)</Label>
            <Textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} rows={8} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => onSave(formData)} disabled={formData.visible_to.length === 0}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Tutorial Dialog Component
const TutorialDialog = ({ open, onOpenChange, tutorial, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    category: 'features',
    content: '',
    order: 0,
    is_published: true,
    visible_to: ['client', 'team_member', 'agency']
  });

  const roleOptions = [
    { value: 'client', label: 'Clients' },
    { value: 'team_member', label: 'Membres équipe' },
    { value: 'agency', label: 'Agence' }
  ];

  useEffect(() => {
    if (tutorial) {
      setFormData({
        title: tutorial.title || '',
        description: tutorial.description || '',
        youtube_url: tutorial.youtube_url || '',
        category: tutorial.category || 'features',
        content: tutorial.content || '',
        order: tutorial.order || 0,
        is_published: tutorial.is_published !== false,
        visible_to: tutorial.visible_to || ['client', 'team_member', 'agency']
      });
    } else {
      setFormData({ title: '', description: '', youtube_url: '', category: 'features', content: '', order: 0, is_published: true, visible_to: ['client', 'team_member', 'agency'] });
    }
  }, [tutorial, open]);

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      visible_to: prev.visible_to.includes(role)
        ? prev.visible_to.filter(r => r !== role)
        : [...prev.visible_to, role]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tutorial ? 'Modifier' : 'Créer'} un tutoriel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Lien YouTube</Label>
            <Input value={formData.youtube_url} onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="getting_started">Démarrage</SelectItem>
                <SelectItem value="features">Fonctionnalités</SelectItem>
                <SelectItem value="tips">Astuces</SelectItem>
                <SelectItem value="advanced">Avancé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visible pour</Label>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map(role => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => toggleRole(role.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                    formData.visible_to.includes(role.value)
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
                  )}
                >
                  {role.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">Sélectionnez les rôles qui pourront voir ce tutoriel</p>
          </div>
          <div className="space-y-2">
            <Label>Contenu complémentaire</Label>
            <Textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} rows={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => onSave(formData)} disabled={formData.visible_to.length === 0}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Version Dialog Component
const VersionDialog = ({ open, onOpenChange, version, onSave }) => {
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    release_date: new Date().toISOString().split('T')[0],
    changes: [],
    is_published: true
  });
  const [newChange, setNewChange] = useState({ type: 'feature', description: '' });

  useEffect(() => {
    if (version) {
      setFormData({
        version: version.version || '',
        title: version.title || '',
        release_date: version.release_date || new Date().toISOString().split('T')[0],
        changes: version.changes || [],
        is_published: version.is_published !== false
      });
    } else {
      setFormData({ version: '', title: '', release_date: new Date().toISOString().split('T')[0], changes: [], is_published: true });
    }
  }, [version, open]);

  const addChange = () => {
    if (!newChange.description) return;
    setFormData(prev => ({ ...prev, changes: [...prev.changes, newChange] }));
    setNewChange({ type: 'feature', description: '' });
  };

  const removeChange = (index) => {
    setFormData(prev => ({ ...prev, changes: prev.changes.filter((_, i) => i !== index) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{version ? 'Modifier' : 'Créer'} une version</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Numéro de version</Label>
              <Input value={formData.version} onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))} placeholder="1.2.0" />
            </div>
            <div className="space-y-2">
              <Label>Date de sortie</Label>
              <Input type="date" value={formData.release_date} onChange={(e) => setFormData(prev => ({ ...prev, release_date: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Nouvelles fonctionnalités" />
          </div>
          <div className="space-y-2">
            <Label>Changements</Label>
            <div className="flex gap-2">
              <Select value={newChange.type} onValueChange={(value) => setNewChange(prev => ({ ...prev, type: value }))}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Nouveau</SelectItem>
                  <SelectItem value="fix">Correction</SelectItem>
                  <SelectItem value="improvement">Amélioration</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                value={newChange.description} 
                onChange={(e) => setNewChange(prev => ({ ...prev, description: e.target.value }))} 
                placeholder="Description du changement"
                className="flex-1"
              />
              <Button type="button" onClick={addChange}><Plus className="w-4 h-4" /></Button>
            </div>
            {formData.changes.length > 0 && (
              <div className="space-y-2 mt-2">
                {formData.changes.map((change, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <Badge className={cn("text-xs",
                      change.type === 'feature' ? 'bg-green-100 text-green-700' :
                      change.type === 'fix' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    )}>
                      {change.type === 'feature' ? 'Nouveau' : change.type === 'fix' ? 'Correction' : 'Amélioration'}
                    </Badge>
                    <span className="flex-1 text-sm">{change.description}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeChange(index)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => onSave(formData)}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminToolsPage;
